import React, { useState, useMemo, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import PopularCalculators from './PopularCalculators';
import CalculatorCategories from './CalculatorCategories';
import SearchBar from './SearchBar';
import Glossary from './Glossary';
import calculators from '../data/calculators';

function HomePage() {
  const siteUrl = 'https://www.marketingcalculatorhub.com';
  const logoUrl = `${siteUrl}/logo.png`; // Replace with your actual logo URL

  useEffect(() => {
    // Add JSON-LD Schema
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify([
      {
        "@context": "https://schema.org",
        "@type": "WebSite",
        "name": "Marketing Calculator Hub",
        "description": "Comprehensive suite of marketing calculators for data-driven strategy optimization",
        "url": siteUrl,
        "potentialAction": {
          "@type": "SearchAction",
          "target": `${siteUrl}/search?q={search_term_string}`,
          "query-input": "required name=search_term_string"
        }
      },
      {
        "@context": "https://schema.org",
        "@type": "Organization",
        "name": "Marketing Calculator Hub",
        "url": siteUrl,
        "logo": logoUrl,
        "description": "Transform your marketing strategy with 60+ free calculators. Calculate ROI, CAC, CLV, conversion rates & more."
      }
    ]);
    document.head.appendChild(script);

    return () => {
      document.head.removeChild(script);
    };
  }, [siteUrl, logoUrl]);

  const [searchTerm, setSearchTerm] = useState('');

  const filteredCalculators = useMemo(() => {
    return calculators.filter(calc => 
      calc.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      calc.description.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [searchTerm]);

  return (
    <>
      <Helmet>
        <title>Marketing Calculator Hub: 60+ Free Marketing Tools & Calculators</title>
        <meta name="description" content="Transform your marketing strategy with 60+ free calculators. Calculate ROI, CAC, CLV, conversion rates & more. Data-driven decisions made simple. Start optimizing now!" />
        <meta name="keywords" content="marketing calculators, ROI calculator, CAC calculator, CLV calculator, conversion rate calculator, digital marketing tools, free marketing calculators, marketing metrics" />
        <link rel="canonical" href={siteUrl} />

        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content={siteUrl} />
        <meta property="og:title" content="Marketing Calculator Hub: 50+ Free Tools for Marketing Success" />
        <meta property="og:description" content="Access 50+ free marketing calculators. Optimize ROI, CPA, and more. Make data-driven decisions to boost your marketing performance. Start now!" />
        <meta property="og:image" content={logoUrl} />

        {/* Twitter */}
        <meta property="twitter:card" content="summary_large_image" />
        <meta property="twitter:url" content={siteUrl} />
        <meta property="twitter:title" content="Marketing Calculator Hub: 50+ Free Tools for Marketing Success" />
        <meta property="twitter:description" content="Access 50+ free marketing calculators. Optimize ROI, CPA, and more. Make data-driven decisions to boost your marketing performance. Start now!" />
        <meta property="twitter:image" content={logoUrl} />

        {/* Schema.org JSON-LD */}
      </Helmet>
      <div className="container mx-auto px-4 py-8">
        <section className="text-center mb-12">
          <h1 className="text-4xl font-bold text-gray-800 mb-4">Marketing Calculator Hub</h1>
          <p className="text-xl text-gray-600 mb-6">
            Optimize your marketing strategies with our suite of 50+ powerful calculators
          </p>
          <SearchBar searchTerm={searchTerm} setSearchTerm={setSearchTerm} />
        </section>

      {searchTerm ? (
        <section className="mb-12">
          <h2 className="text-2xl font-semibold text-gray-800 mb-6">Search Results</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredCalculators.map((calc) => (
              <div key={calc.id} className="bg-white rounded-lg shadow-md p-6">
                <h3 className="text-lg font-semibold text-gray-800 mb-2">{calc.name}</h3>
                <p className="text-gray-600 mb-4">{calc.description}</p>
                <Link to={calc.path} className="text-indigo-600 hover:text-indigo-800">Use Calculator</Link>
              </div>
            ))}
          </div>
        </section>
      ) : (
        <>
          <PopularCalculators />
          <CalculatorCategories />
        </>
      )}

        <Glossary />
      </div>
    </>
  );
}

export default HomePage;
